import React, { useEffect, useState, useMemo } from "react";
import { Field, useFormikContext, FieldArray } from "formik";
import { useDispatch } from "react-redux";
import {
  getMainModulesAction,
  getSubModulesAction,
  getColumnsAction,
} from "../../redux/actions/approval-work-flow-action";
import errorToast from "../../components/helper-components/error-toast";
import LoadingOverlay from "../../components/helper-components/overlay-loading";

export default function DocumentSection() {
  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [DOCUMENT_TYPES, setDOCUMENT_TYPES] = useState({});
  const [DOCUMENT_TYPES_ID, setDOCUMENT_TYPES_ID] = useState({});
  const [fieldConfigMap, setFieldConfigMap] = useState({});

  const docTypes = DOCUMENT_TYPES[values.documentCategory] || [];
  const config = useMemo(
    () => fieldConfigMap[values.documentType] || [],
    [fieldConfigMap, values.documentType]
  );

  const onError = (data) => {
    // setIsLoading(false);
    errorToast(data?.data?.message || "Something went wrong");
  };

  // --- MAIN MODULES ---
  useEffect(() => {
    if (Object.keys(DOCUMENT_TYPES).length > 0) return; // skip re-fetch on remount
    // setIsLoading(true);
    dispatch(
      getMainModulesAction(
        {},
        (data) => {
          const modules = data?.data || [];
          const types = modules.reduce((acc, { main_module_name }) => {
            acc[main_module_name] = [];
            return acc;
          }, {});
          const ids = modules.reduce((acc, { main_module_name, main_module_id }) => {
            acc[main_module_name] = main_module_id;
            return acc;
          }, {});
          setDOCUMENT_TYPES(types);
          setDOCUMENT_TYPES_ID(ids);
          // setIsLoading(false);
        },
        onError
      )
    );
  }, [dispatch]);

  // --- SUBMODULES (DOCUMENT TYPES) ---
  useEffect(() => {
    const category = values.documentCategory;
    if (!category) return;
    const moduleId = DOCUMENT_TYPES_ID?.[category];
    if (!moduleId) return;

    if (DOCUMENT_TYPES?.[category]?.length) return; // already loaded

    // setIsLoading(true);
    dispatch(
      getSubModulesAction(
        { module_id: moduleId },
        (res) => {
          const submodules = (res?.data || []).map((m) => m.module_name);
          setDOCUMENT_TYPES((prev) => ({ ...prev, [category]: submodules }));
          // setIsLoading(false);
        },
        onError
      )
    );
  }, [values.documentCategory]);

  // --- FIELDS (COLUMNS) ---
  useEffect(() => {
    const type = values.documentType;
    if (!type) return;
    if (fieldConfigMap[type]?.length) return;

    // setIsLoading(true);
    dispatch(
      getColumnsAction(
        { transaction_type: type },
        (res) => {
          const cols = res?.data || [];
          const mapped = cols
            .filter(
              (c) =>
                c?.column_name &&
                !String(c.column_name).toLowerCase().endsWith("id")
            )
            .map((c) => ({
              field: c.column_name,
              aggregators: ["<", ">", "=", "!="],
              values: "string",
            }));
          setFieldConfigMap((prev) => ({ ...prev, [type]: mapped }));
          // setIsLoading(false);
        },
        onError
      )
    );
  }, [values.documentType]);

  // --- INITIALIZE ONLY IF EMPTY ---
  useEffect(() => {
    if (!values.documentType || !config.length) return;
    // Don't overwrite existing user data
    if (values.conditions && values.conditions.length > 0) return;

    const newConditions = config.map((f) => ({
      enabled: false,
      field: f.field,
      aggregator: "",
      value: "",
    }));
    setFieldValue("conditions", newConditions);
  }, [values.documentType, config]);

  // --- ONLY ENABLED CONDITIONS TRACKED ---
  useEffect(() => {
    const enabledConditions = (values.conditions || []).filter((c) => c.enabled);
    setFieldValue("enabledConditions", enabledConditions);
  }, [values.conditions]);

  const handleEnableToggle = (idx, checked) => {
    const updated = [...(values.conditions || [])];
    updated[idx].enabled = checked;
    if (!checked) {
      updated[idx].aggregator = "";
      updated[idx].value = "";
    }
    setFieldValue("conditions", updated);
  };

  // --- SELF-REHYDRATE DOC TYPES LIST ON REMOUNT ---
  useEffect(() => {
    if (
      values.documentCategory &&
      (!DOCUMENT_TYPES[values.documentCategory] ||
        DOCUMENT_TYPES[values.documentCategory].length === 0)
    ) {
      const moduleId = DOCUMENT_TYPES_ID?.[values.documentCategory];
      if (moduleId) {
        dispatch(
          getSubModulesAction(
            { module_id: moduleId },
            (res) => {
              const subs = (res?.data || []).map((m) => m.module_name);
              setDOCUMENT_TYPES((prev) => ({
                ...prev,
                [values.documentCategory]: subs,
              }));
            },
            onError
          )
        );
      }
    }
  }, [values.documentCategory, DOCUMENT_TYPES]);

  return (
    <>
      <LoadingOverlay isLoading={isLoading} />
      <div className="flex flex-col md:flex-row bg-white rounded-lg gap-6 text-sm text-black h-[95%] max-h-[95%] w-full">
        {/* LEFT PANEL */}
        <div className="flex-shrink-0 md:w-80 border border-gray-200 rounded-lg p-6 bg-gray-50">
          <label className="text-sm text-gray-600 mb-2 block">
            Select Document Category
          </label>
          <Field
            as="select"
            name="documentCategory"
            onChange={(e) => {
              const val = e.target.value;
              setFieldValue("documentCategory", val);
              setFieldValue("documentType", "");
              setFieldValue("conditions", []);
            }}
            className="w-full border border-[var(--toggle-switch-off-bg)] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary bg-white"
          >
            <option value="">Select Document Category</option>
            {Object.keys(DOCUMENT_TYPES)?.map((cat) => (
              <option key={cat} value={cat}>
                {cat
                  ?.split("_")
                  ?.join(" ")
                  ?.replace(/^\w/, (c) => c.toUpperCase())}
              </option>
            ))}
          </Field>

          {values.documentCategory && (
            <>
              <label className="text-sm text-gray-600 mt-6 mb-3 block">
                Select Document Types
              </label>
              <div className="flex flex-col space-y-3 overflow-y-auto max-h-[76%] p-2">
                {docTypes?.map((type) => (
                  <label
                    key={type}
                    className="flex items-center gap-2 cursor-pointer text-gray-700"
                  >
                    <Field
                      type="radio"
                      name="documentType"
                      value={type}
                      checked={values.documentType === type}
                      onChange={() => setFieldValue("documentType", type)}
                      className="w-4 h-4 accent-primary focus:ring-primary"
                    />
                    <span className="text-sm">
                      {type
                        ?.split("_")
                        ?.join(" ")
                        ?.replace(/^\w/, (c) => c.toUpperCase())}
                    </span>
                  </label>
                ))}
                {/* fallback if docType exists but list empty */}
                {values.documentType &&
                  !docTypes.includes(values.documentType) && (
                    <label className="flex items-center gap-2 text-gray-700">
                      <input
                        type="radio"
                        checked
                        readOnly
                        className="w-4 h-4 accent-primary focus:ring-primary"
                      />
                      <span className="text-sm">
                        {values.documentType
                          ?.split("_")
                          ?.join(" ")
                          ?.replace(/^\w/, (c) => c.toUpperCase())}
                      </span>
                    </label>
                  )}
              </div>
            </>
          )}
        </div>

        {/* RIGHT PANEL */}
        <div className="flex-1 p-6 min-w-[70%]">
          <label className="font-medium mb-4 block text-base">
            Launch Approval Procedure :
          </label>

          <div className="flex flex-col space-y-3 mb-6">
            <label className="flex items-center gap-2 cursor-pointer text-gray-700">
              <Field
                type="checkbox"
                name="isAlways"
                value="always"
                // disabled={values.documentType === ""}
                disabled={true}
                className="w-4 h-4 rounded-md border border-primary cursor-pointer accent-[var(--primary)]"
              />
              <span className="text-sm">Always</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer text-gray-700">
              <Field
                type="checkbox"
                name="isRule"
                value="conditional"
                disabled={values.documentType === ""}
                className="w-4 h-4 rounded-md border border-primary cursor-pointer accent-[var(--primary)]"
              />
              <span className="text-sm">When the Following Applies</span>
            </label>
          </div>

          {values?.isRule?.[0] === "conditional" && (
            <FieldArray name="conditions">
              {() => (
                <div className="border border-gray-200 rounded-lg w-full overflow-hidden">
                  <div className="max-h-[320px] overflow-y-auto max-w-full overflow-x-auto">
                    <table className="min-w-full border-collapse text-sm">
                      <thead className="bg-[var(--list-table-heading-bg)] text-white sticky top-0 z-10">
                        <tr className="text-center text-gray-700">
                          <th className="px-4 py-3 font-medium w-[10%]">
                            CHOOSE
                          </th>
                          <th className="px-4 py-3 font-medium w-[25%]">
                            FIELD
                          </th>
                          <th className="px-4 py-3 font-medium w-[20%]">
                            AGGREGATOR
                          </th>
                          <th className="px-4 py-3 font-medium w-[25%]">
                            VALUE
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {(values.conditions || []).map((cond, idx) => {
                          const selectedField = (config || []).find(
                            (f) => f.field === cond.field
                          );
                          return (
                            <tr
                              key={idx}
                              className="border-t border-gray-200 hover:bg-gray-50"
                            >
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={cond.enabled}
                                  onChange={(e) =>
                                    handleEnableToggle(idx, e.target.checked)
                                  }
                                  className="w-4 h-4 rounded-md border border-primary cursor-pointer accent-[var(--primary)]"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={cond.field}
                                  readOnly
                                  className="w-full border border-gray-200 bg-gray-100 rounded-md px-3 py-2 text-sm text-black cursor-not-allowed"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Field
                                  as="select"
                                  name={`conditions[${idx}].aggregator`}
                                  disabled={!cond.enabled}
                                  className="w-full border border-[var(--toggle-switch-off-bg)] rounded-md px-3 py-2 text-sm"
                                >
                                  <option value="">Select Aggregator</option>
                                  {(selectedField?.aggregators || []).map(
                                    (a) => (
                                      <option key={a} value={a}>
                                        {a}
                                      </option>
                                    )
                                  )}
                                </Field>
                              </td>
                              <td className="px-4 py-3">
                                {selectedField?.values === "string" ? (
                                  <Field
                                    type="text"
                                    name={`conditions[${idx}].value`}
                                    placeholder="Enter value"
                                    disabled={!cond.enabled}
                                    className="w-full border border-[var(--toggle-switch-off-bg)] rounded-md px-3 py-2 text-sm"
                                  />
                                ) : selectedField?.values === "number" ? (
                                  <Field
                                    type="number"
                                    name={`conditions[${idx}].value`}
                                    placeholder="Enter number"
                                    disabled={!cond.enabled}
                                    className="w-full border border-[var(--toggle-switch-off-bg)] rounded-md px-3 py-2 text-sm"
                                  />
                                ) : (
                                  <Field
                                    as="select"
                                    name={`conditions[${idx}].value`}
                                    disabled={!cond.enabled}
                                    className="w-full border border-[var(--toggle-switch-off-bg)] rounded-md px-3 py-2 text-sm"
                                  >
                                    <option value="">Select Value</option>
                                    {(selectedField?.values || []).map?.((v) => (
                                      <option key={v} value={v}>
                                        {v}
                                      </option>
                                    ))}
                                  </Field>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </FieldArray>
          )}
        </div>
      </div>
    </>
  );
}
